/**
 * Visual Config — Color palettes.
 *
 * Light and dark mode color arrays for each palette option.
 */
import type { PaletteOption } from './types';

// ── Light mode palettes ────────────────────────────────────────────────────

export const PALETTE_COLORS: Record<PaletteOption, string[]> = {
  default: [
    '#3498db', '#2ecc71', '#f1c40f', '#9b59b6', '#e67e22',
    '#1abc9c', '#34495e', '#e74c3c', '#95a5a6', '#16a085',
  ],
  monochrome: [
    '#1a1a2e', '#2d2d4a', '#404066', '#535382', '#66669e',
    '#7979ba', '#8c8cd6', '#9f9ff2', '#b2b2ff', '#c5c5ff',
  ],
  warm: [
    '#e74c3c', '#e67e22', '#f1c40f', '#f39c12', '#d35400',
    '#c0392b', '#e74c3c', '#ff6348', '#ff7979', '#ffbe76',
  ],
  cool: [
    '#3498db', '#2980b9', '#1abc9c', '#16a085', '#2ecc71',
    '#27ae60', '#0984e3', '#00b894', '#00cec9', '#81ecec',
  ],
  pastel: [
    '#a29bfe', '#74b9ff', '#55efc4', '#ffeaa7', '#fab1a0',
    '#fd79a8', '#fdcb6e', '#e17055', '#00b894', '#e84393',
  ],
  colorblind: [
    '#0072B2', '#E69F00', '#009E73', '#CC79A7', '#56B4E9',
    '#D55E00', '#F0E442', '#000000', '#999999', '#648FFF',
  ],
};

// ── Dark mode palettes ─────────────────────────────────────────────────────

export const PALETTE_COLORS_DARK: Record<PaletteOption, string[]> = {
  default: [
    '#5dade2', '#58d68d', '#f7dc6f', '#bb8fce', '#f8c471',
    '#85c1e9', '#82e0aa', '#f9e79f', '#d2b4de', '#fadbd8',
  ],
  monochrome: [
    '#4a4a6a', '#5d5d86', '#7070a2', '#8383be', '#9696da',
    '#a9a9f6', '#bcbcff', '#cfcfff', '#e2e2ff', '#f5f5ff',
  ],
  warm: [
    '#ff6b6b', '#ff9f43', '#feca57', '#ff9ff3', '#f368e0',
    '#ee5a24', '#ff6348', '#ff7979', '#ffbe76', '#ffd32a',
  ],
  cool: [
    '#54a0ff', '#48dbfb', '#0abde3', '#1dd1a1', '#10ac84',
    '#00d2d3', '#2e86de', '#01a3a4', '#00cec9', '#81ecec',
  ],
  pastel: [
    '#c8b6ff', '#90cdf4', '#81ecec', '#ffeead', '#ffb8b8',
    '#ff9ff3', '#ffe066', '#ff8a5c', '#55efc4', '#fd79a8',
  ],
  colorblind: [
    '#56B4E9', '#F0E442', '#009E73', '#E69F00', '#0072B2',
    '#D55E00', '#CC79A7', '#BBBBBB', '#CCCCCC', '#82AAFF',
  ],
};

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Get the color array for a palette + dark mode combination.
 */
export function getPaletteColors(palette: PaletteOption, isDark: boolean): string[] {
  return isDark
    ? (PALETTE_COLORS_DARK[palette] ?? PALETTE_COLORS_DARK.default)
    : (PALETTE_COLORS[palette] ?? PALETTE_COLORS.default);
}

// ── Heatmap color gradient ─────────────────────────────────────────────────

/**
 * Lighten a hex color by mixing it with white.
 * `amount` 0 = original, 1 = white.
 */
function lightenHex(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`;
}

/**
 * Get a sequential gradient [light, dark] for a heatmap
 * based on a palette color at the given index.
 */
export function getHeatmapColorGradient(
  palette: PaletteOption,
  colorIndex: number,
): [string, string] {
  const colors = PALETTE_COLORS[palette] ?? PALETTE_COLORS.default;
  const idx = Math.max(0, Math.min(colorIndex, colors.length - 1));
  const baseColor = colors[idx];
  return [lightenHex(baseColor, 0.85), baseColor];
}

// ── Palette picker options (preview swatches for the UI) ───────────────────

export interface PalettePickerOption {
  value: PaletteOption;
  label: string;
  colors: string[];
}

export const PALETTE_OPTIONS: PalettePickerOption[] = [
  {
    value: 'default',
    label: 'Default',
    colors: ['#3498db', '#2ecc71', '#f1c40f', '#9b59b6', '#e67e22'],
  },
  {
    value: 'monochrome',
    label: 'Monochrome',
    colors: ['#1a1a2e', '#404066', '#66669e', '#8c8cd6', '#b2b2ff'],
  },
  {
    value: 'warm',
    label: 'Warm',
    colors: ['#e74c3c', '#e67e22', '#f1c40f', '#f39c12', '#d35400'],
  },
  {
    value: 'cool',
    label: 'Cool',
    colors: ['#3498db', '#2980b9', '#1abc9c', '#16a085', '#2ecc71'],
  },
  {
    value: 'pastel',
    label: 'Pastel',
    colors: ['#a29bfe', '#74b9ff', '#55efc4', '#ffeaa7', '#fab1a0'],
  },
  {
    value: 'colorblind',
    label: 'Colorblind',
    colors: ['#0072B2', '#E69F00', '#009E73', '#CC79A7', '#56B4E9'],
  },
];